import type { Offer } from '../types/offerTypes';
import type { Site } from '../types/siteTypes';

/**
 * Corrects offer.siteId to match the frontend Site array, so offers can be reliably
 * filtered/grouped by site everywhere (Compare & Select, Cost Analysis, proposal
 * generation). Needed because backend getOffers() resolves offer.siteId to the REAL
 * site.id, while Site.id in this app is actually the tender_site JUNCTION row id
 * (see siteTypes.ts — Site.siteId is the real FK). Without this correction, any
 * code comparing offer.siteId to site.id directly silently fails to match.
 *
 * Strategy 1 (primary): match on the real site id — site.siteId, falling back to
 * site.id for any Site record that doesn't carry siteId separately.
 * Strategy 2 (fallback): match by site name, but ONLY when unambiguous (two sites
 * sharing a name — e.g. two branches of the same business — must not silently
 * collapse onto whichever is found first).
 * Strategy 3: leave the offer's siteId untouched and log a warning.
 */
export function mapOffersWithSites(offersToMap: Offer[], sites: Site[]): Offer[] {
  if (sites.length === 0) {
    console.warn('⚠️ mapOffersWithSites: sites not loaded yet — returning offers unchanged');
    return offersToMap;
  }

  return offersToMap.map((dbOffer: any) => {
    const matchingSiteById = sites.find(s => (s.siteId || s.id) === dbOffer.siteId);
    if (matchingSiteById) {
      return {
        ...dbOffer,
        siteId: matchingSiteById.id,
        siteName: matchingSiteById.name || dbOffer.siteName,
      };
    }

    const sitesWithMatchingName = sites.filter(s => s.name === dbOffer.siteName);
    if (sitesWithMatchingName.length === 1) {
      const matchingSiteByName = sitesWithMatchingName[0];
      console.log(`📌 mapOffersWithSites: matched offer site by name: ${dbOffer.siteName} -> ${matchingSiteByName.id}`);
      return {
        ...dbOffer,
        siteId: matchingSiteByName.id,
        siteName: matchingSiteByName.name,
      };
    }
    if (sitesWithMatchingName.length > 1) {
      console.warn(`⚠️ mapOffersWithSites: ambiguous site name "${dbOffer.siteName}" matches ${sitesWithMatchingName.length} sites for offer ${dbOffer.id} — refusing to guess, keeping original siteId.`);
    }

    console.warn(`⚠️ mapOffersWithSites: could not match site for offer ${dbOffer.id} (siteId: ${dbOffer.siteId}, siteName: ${dbOffer.siteName})`);
    return dbOffer;
  });
}
